import React, { useCallback, useEffect, useMemo, useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "./lightbox.css";

/* =====================================================================
   GLOBÁLNÍ LIGHTBOX

   Nemusí se zapojovat do jednotlivých komponent – poslouchá kliknutí
   na celém dokumentu a otevře zvětšenou fotku u každého <img>, který:

     - není uvnitř odkazu / tlačítka (ty mají vlastní akci)
     - nemá data-no-lightbox (ani na žádném rodiči)
     - není logo ani ikona (podle src / alt)
     - není dekorativní pozadí (position: absolute/fixed)
     - je dost velký, aby šlo o fotku (min. 80 × 80 px)

   Fotky ve stejném kontejneru (grid galerie) se dají prolistovat
   šipkami / swipem. Zoom funguje kolečkem, gestem i dvojklikem –
   bez tlačítek, aby zůstal čistý vzhled podle zadání.
   ===================================================================== */

const MIN_SIZE = 80;

type Slide = { src: string; alt: string; description?: string };

const isEligible = (el: Element | null): el is HTMLImageElement => {
  if (!el || el.tagName !== "IMG") return false;
  const img = el as HTMLImageElement;

  // vlastní akce elementu má přednost
  if (img.closest("a, button, [role='button'], label, [data-no-lightbox]")) {
    return false;
  }

  const src = img.currentSrc || img.src;
  if (!src || src.startsWith("data:")) return false;

  // loga a ikony nejsou fotky
  if (/logo|icon|ikona/i.test(src) || /logo|icon|ikona/i.test(img.alt || "")) {
    return false;
  }

  // dekorativní pozadí (hero sekce apod.) nezvětšujeme
  const position = window.getComputedStyle(img).position;
  if (position === "absolute" || position === "fixed") return false;

  const rect = img.getBoundingClientRect();
  return rect.width >= MIN_SIZE && rect.height >= MIN_SIZE;
};

/** Nejbližší rodič, který obsahuje víc fotek – tedy galerie, v níž se listuje. */
const findGallery = (img: HTMLImageElement): HTMLElement => {
  let node: HTMLElement | null = img.parentElement;
  let fallback: HTMLElement = img.parentElement ?? document.body;

  while (node && node !== document.body) {
    const count = Array.from(node.querySelectorAll("img")).filter(isEligible).length;
    if (count > 1) return node;
    fallback = node;
    node = node.parentElement;
  }

  return fallback;
};

const toSlide = (img: HTMLImageElement): Slide => {
  const alt = img.alt?.trim() ?? "";
  return {
    src: img.getAttribute("data-full") || img.currentSrc || img.src,
    alt,
    description: alt || undefined,
  };
};

const LightboxProvider: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [index, setIndex] = useState(0);

  /* ---------- otevření kliknutím (delegovaně na celém dokumentu) ---------- */
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target as Element | null;
      if (!isEligible(target)) return;

      const img = target;
      const gallery = findGallery(img);
      const images = Array.from(gallery.querySelectorAll("img")).filter(isEligible);
      const clicked = Math.max(0, images.indexOf(img));

      setSlides(images.map(toSlide));
      setIndex(clicked);
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  /* ---------- kurzor „lupa“ u fotek, které jdou zvětšit ---------- */
  useEffect(() => {
    let frame = 0;

    const mark = () => {
      frame = 0;
      document.querySelectorAll("img:not([data-lightbox])").forEach((el) => {
        if (isEligible(el)) el.setAttribute("data-lightbox", "true");
      });
    };

    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(mark);
    };

    schedule();

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("load", schedule);
    window.addEventListener("resize", schedule);

    return () => {
      observer.disconnect();
      window.removeEventListener("load", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const close = useCallback(() => setSlides([]), []);

  const plugins = useMemo(() => [Counter, Zoom], []);

  if (slides.length === 0) return null;

  return (
    <Lightbox
      className="hpr-lightbox"
      open
      close={close}
      index={index}
      on={{ view: ({ index: current }) => setIndex(current) }}
      slides={slides}
      plugins={plugins}
      carousel={{ finite: slides.length <= 1, padding: 0 }}
      controller={{ closeOnBackdropClick: true }}
      animation={{ fade: 250, swipe: 400 }}
      zoom={{ maxZoomPixelRatio: 3, doubleTapDelay: 300, scrollToZoom: true }}
      toolbar={{ buttons: ["close"] }}
      /* zoom zůstává (kolečko, gesto, dvojklik), jen bez tlačítek v liště */
      render={{ buttonZoom: () => null }}
      labels={{
        Previous: "Předchozí fotka",
        Next: "Další fotka",
        Close: "Zavřít",
      }}
    />
  );
};

export default LightboxProvider;
